const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const containsAny = (text, terms) => terms.some((term) => text.includes(term));

const inferFuelType = (combined) => {
  const dieselCues = ['diesel', 'tdi', 'dci', 'hdi', 'cdi', 'crdi', 'd-4d', 'd4d', 'duratorq', 'gd-6', 'gd6', 'ddi', 'td'];
  const petrolCues = ['petrol', 'tsi', 'tfsi', 'vtec', 'ecoboost', 'gdi', 'mpi'];
  const petrolOnlyFamilies = [
    'picanto', 'i10', 'i20', 'yaris', 'swift', 'fit', 'jazz', 'corolla', 'corolla cross',
    'mazda2', 'mazda 2', 'mazda3', 'mazda 3', 'micra', 'polo vivo'
  ];
  const dieselLikelyFamilies = [
    'bakkie', 'hilux', 'ranger', 'navara', 'amarok', 'd-max', 'dmax', 'sprinter', 'vito',
    'panel van', 'truck', 'fortuner', 'mu-x', 'mux', 'sorento'
  ];

  if (containsAny(combined, dieselCues)) return 'Diesel';
  if (containsAny(combined, petrolCues)) return 'Petrol';
  if (containsAny(combined, petrolOnlyFamilies)) return 'Petrol';
  if (containsAny(combined, dieselLikelyFamilies)) return 'Diesel';
  return 'Petrol';
};

export const estimateGeneralFuelConsumption = ({ make = '', model = '', fuelType = 'Petrol' }) => {
  const combined = `${make} ${model}`.toLowerCase().trim();

  if (!combined) {
    return {
      value: fuelType === 'Diesel' ? 7.0 : 8.0,
      reason: 'Using class baseline because make/model is empty.'
    };
  }

  if (containsAny(combined, ['ev', 'electric', 'tesla'])) {
    return {
      value: 0,
      reason: 'Detected electric vehicle; fuel consumption is not applicable.'
    };
  }

  let estimate = fuelType === 'Diesel' ? 7.0 : 8.2;

  if (containsAny(combined, ['hatch', 'i10', 'i20', 'polo', 'yaris', 'picanto', 'swift', 'fit'])) {
    estimate -= 1.0;
  }
  if (containsAny(combined, ['sedan', 'corolla', 'civic', 'jetta', 'mazda 3', 'elantra'])) {
    estimate += 0.3;
  }
  if (containsAny(combined, ['suv', 'rav4', 'crv', 'x-trail', 'xtrail', 'tucson', 'sportage', 'fortuner'])) {
    estimate += 1.8;
  }
  if (containsAny(combined, ['bakkie', 'hilux', 'ranger', 'd-max', 'dmax', 'navara', 'amarok'])) {
    estimate += 2.3;
  }
  if (containsAny(combined, ['v6', 'v8', 'amg', 'gti', 'm3', 'turbo'])) {
    estimate += 1.4;
  }
  if (containsAny(combined, ['hybrid'])) {
    estimate -= 1.8;
  }

  const engineMatch = combined.match(/(\d\.\d)\s?l?/);
  if (engineMatch) {
    const liters = Number(engineMatch[1]);
    if (!Number.isNaN(liters)) {
      if (liters >= 3.0) estimate += 1.8;
      else if (liters >= 2.0) estimate += 1.0;
      else if (liters <= 1.2) estimate -= 0.5;
    }
  }

  const bounded = fuelType === 'Diesel'
    ? clamp(estimate, 4.0, 15.0)
    : clamp(estimate, 4.5, 18.0);

  return {
    value: Number(bounded.toFixed(1)),
    reason: 'Estimated from vehicle type keywords and fuel type.'
  };
};

export const inferVehicleProfileFromDetails = ({ make = '', model = '' }) => {
  const combined = `${make} ${model}`.toLowerCase().trim();

  if (!combined) {
    return {
      fuelType: 'Petrol',
      vehicleClass: 'Class 1',
      avgConsumption: 8.0,
      reason: 'Using defaults while vehicle details are empty.'
    };
  }

  const fuelType = inferFuelType(combined);

  let vehicleClass = 'Class 1';
  if (combined.includes('2 axle') || combined.includes('2-axle')) {
    vehicleClass = 'Class 2';
  } else if (combined.includes('3 axle') || combined.includes('3-axle') || combined.includes('4 axle') || combined.includes('4-axle')) {
    vehicleClass = 'Class 3';
  } else if (
    combined.includes('5 axle') ||
    combined.includes('5-axle') ||
    combined.includes('6 axle') ||
    combined.includes('6-axle') ||
    combined.includes('truck') ||
    combined.includes('lorry') ||
    combined.includes('articulated')
  ) {
    vehicleClass = 'Class 4';
  }

  if (vehicleClass === 'Class 1') {
    if (
      combined.includes('minibus') ||
      combined.includes('sprinter') ||
      combined.includes('panel van') ||
      combined.includes('bakkie') ||
      combined.includes('hilux') ||
      combined.includes('ranger') ||
      combined.includes('navara') ||
      combined.includes('amarok') ||
      combined.includes('d-max') ||
      combined.includes('dmax')
    ) {
      vehicleClass = 'Class 2';
    }
  }

  const estimate = estimateGeneralFuelConsumption({ make, model, fuelType });

  return {
    fuelType,
    vehicleClass,
    avgConsumption: estimate.value,
    reason: `${estimate.reason} Vehicle class inferred from model keywords.`
  };
};
